import React from 'react';
import { createTheme, alpha } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';

export const fontFamily = 'Atyp Display';
export const fontFamilySecondary = 'Athelas';

export const agentTheme = createTheme({
  typography: {
    fontFamily,
    fontFamilySecondary,
    h1: {
      fontFamily: fontFamilySecondary,
      fontSize: '56px',
      fontWeight: 700,
    },
    h2: {
      fontFamily: fontFamilySecondary,
      fontSize: '48px',
      fontWeight: 700,
    },
    h3: {
      fontFamily: fontFamilySecondary,
      fontSize: '42px',
      fontWeight: 700,
    },
    h4: {
      fontFamily: fontFamilySecondary,
      fontSize: '36px',
      fontWeight: 700,
    },
    h5: {
      fontFamily: fontFamilySecondary,
      fontSize: '32px',
      fontWeight: 700,
    },
    h6: {
      fontFamily: fontFamilySecondary,
      fontSize: '24px',
      fontWeight: 700,
    },
    body1: {
      fontFamily: fontFamily,
      fontSize: '16px',
      fontWeight: 400,
    },
    body2: {
      fontFamily: fontFamily,
      fontSize: '12px',
      fontWeight: 400,
    },
    subtitle1: {
      fontFamily: fontFamily,
      fontSize: '14px',
      fontWeight: 400,
    },
    rsp: {
      color: '#B6A28F',
      fontSize: 12,
    },
    label: {
      color: 'rgba(0, 0, 0, 0.5)',
      fontSize: 12,
      component: 'label',
    },
  },
  palette: {
    text: {
      primary: '#070606', // on-text-black
      secondary: '#928272', // darkSand.black20 / on-text-sand
      disabled: '#d3d2d2', // darkGreen.white80 / on-text-inverse
    },
    divider: '#c5b5a5', // darkSand.white20 / surface/border
    action: {
      active: 'rgba(0,0,0,0.54)',
      hover: 'rgba(0,0,0,0.04)',
      selected: 'rgba(0,0,0,0.08)',
      disabled: 'rgba(0,0,0,0.26)',
      disabledBackground: 'rgba(0,0,0,0.12)',
    },
    background: {
      warm: '#F7F4F0',
    },
    primary: {
      main: '#123438',
    },
    secondary: {
      main: '#FF6E00',
    },
    success: {
      main: '#4DAA62',
      contrastText: '#fff',
    },
    warning: {
      main: '#cc5800', // on-text-link
    },
    link: {
      main: '#000',
    },
    error: {
      main: '#C34747',
    },
    darkGreen: {
      // The two interaction states the design system draws for a dark-green button. Neither sits on
      // the tonal ramp below, so neither can be reached by picking a step.
      hover: '#1C454B', // Misc/1C454B
      pressed: '#0A2326', // Misc/0A2326
      black80: '#071516',
      black60: '#091A1C',
      black40: '#0B1F22',
      black20: '#0E2A2D',
      base: '#123438',
      white20: '#415D60',
      white40: '#718588',
      white60: '#A0AEAF',
      white80: '#D0D6D7',
    },
    greyBlue: {
      black80: '#101315',
      black60: '#21262A',
      black40: '#313840',
      black20: '#424B55',
      base: '#525E6A',
      main: '#525E6A',
      white20: '#757E88',
      white40: '#979EA6',
      white60: '#BABFC3',
      white80: '#DCDFE1',
    },
    errorRed: {
      // The glyph the design system paints on an error panel. It sits off the ramp below, so it
      // carries a role key.
      solid: '#FF2626', // Misc/FF2626
      black80: '#270E0E',
      black60: '#4E1C1C',
      black40: '#752B2B',
      black20: '#9C3939',
      base: '#C34747',
      white20: '#CF6C6C',
      white40: '#DB9191',
      white60: '#E7B5B5',
      white80: '#FCDADA',
    },
    successGreen: {
      // The fill the design system paints on a button whose service is already added.
      solid: '#1B7D46', // Misc/1B7D46
      black80: '#0F2214',
      black60: '#1F4427',
      black40: '#2E663B',
      black20: '#3E884E',
      base: '#4DAA62',
      white20: '#71BB81',
      white40: '#94CCA1',
      white60: '#B8DDC0',
      white80: '#DBEEE0',
    },
    darkSand: {
      black80: '#24201D',
      black60: '#494139',
      black40: '#6D6156',
      black20: '#928272',
      main: '#B6A28F',
      base: '#B6A28F',
      white20: '#C5B5A5',
      white40: '#D3C7BC',
      white60: '#E2DAD2',
      white80: '#F0ECE9',
    },
    // The cool neutrals the Figma design system paints, keyed by its own numbers. The brand ramps
    // above are warm, so a sand tone cannot stand in for one of these. Separate from MUI's `grey`,
    // whose values differ and whose consumers sit outside this design.
    coolGrey: {
      100: '#F3F4F6', // Surface/gray light
      200: '#E5E7EB', // Border/default
      400: '#9CA3AF', // Text/tertiary
      500: '#6B7280', // Text/secondary
      900: '#111827', // Text/primary dark
      // The stroke the design system draws around a day bucket and around each service row in it.
      borderMuted: '#DADADA', // Border/muted
      // The muted surface the design system paints on a remove control. The Figma file names these
      // three under `Misc`, not under its cool-grey ramp, so they carry role keys and not numbers.
      removeSurface: '#EBEDF0', // Misc/EBEDF0
      removeBorder: '#BFC4C9', // Misc/BFC4C9
      removeText: '#4D545C', // Misc/4D545C
      // A dead neutral rather than a cool grey, so it carries a role key and no ramp number.
      iconMuted: '#CFCFCF', // Icon/muted
      // Also a dead neutral. It is the rule the design system draws inside a card.
      surfaceGray: '#E6E6E6', // Surface/gray
      // The fill the design system paints on a skeleton bar while a panel loads.
      skeleton: '#E0E0E0', // Border/light
      // The glyph it paints on an empty-state panel. Off the ramp, so it carries a role key.
      iconEmptyState: '#8C919E', // Misc/8C919E
      // The track it draws under a scrolling rail.
      surfaceBorder: 'rgba(0, 0, 0, 0.1)', // Container/Surface/border
      // The arrow glyph that marks an extra as indented under its parent service. The Figma asset
      // bakes this stroke in as a raw hex, and no variable in the file binds it.
      iconEntry: '#666666',
    },
    // The warm grey ramp the design system uses for a subtitle. It is not the cool grey ramp.
    neutralGrey: {
      400: '#9B9892', // Neutral Grey/400
      // The thumb the design system draws on a rail's scrollbar.
      800: '#1C1C1C', // Neutral Grey/800
    },
    stoneGrey: {
      black80: '#302F2E',
      black60: '#5F5E5C',
      black40: '#8F8D89',
      black20: '#BEBCB7',
      base: '#EEEBE5',
      white20: '#F1EFEA',
      white40: '#F5F3EF',
      white60: '#F8F7F5',
      white80: '#FCFBFA',
    },
    vividOrange: {
      black80: '#331600',
      black60: '#662C00',
      black40: '#994200',
      black20: '#CC4700',
      base: '#FF6E00',
      // The cream the design system paints behind an active orange icon.
      surface: '#FFFBEC', // Misc/FFFBEC
      // The deeper cream it paints under the pointer.
      surfaceHover: '#FFEDD6', // Misc/FFEDD6
      // The label it paints on a row the traveller may drop. Off the ramp, so it takes a role key.
      optionalTag: '#E5731A', // Misc/e5731a
      white20: '#FF8B33',
      white40: '#FFA866',
      white60: '#FFC599',
      white80: '#FFE2CC',
    },
    darkBrown: {
      black80: '#070606',
      black60: '#0E0C0D',
      black40: '#141313',
      black20: '#1B191A',
      base: '#221F20',
      white20: '#4E4C4D',
      white40: '#7A7979',
      white60: '#A7A5A6',
      white80: '#D3D2D2',
    },
    infoYellow: {
      black80: '#4A3200',
      black60: '#825000',
      black40: '#AA7708',
      black20: '#D59918',
      base: '#FFBE33',
      white20: '#FFD06B',
      white40: '#FFD884',
      white60: '#FFE7B4',
      white80: '#FFF3D9',
    },
    black: {
      100: '#000000',
      80: 'rgba(0,0,0,0.8)',
      60: 'rgba(0,0,0,0.6)',
      40: 'rgba(0,0,0,0.4)',
      20: 'rgba(0,0,0,0.2)',
      10: 'rgba(0,0,0,0.1)',
    },
    white: {
      100: '#FFFFFF',
      80: 'rgba(255,255,255,0.8)',
      60: 'rgba(255,255,255,0.6)',
      40: 'rgba(255,255,255,0.4)',
      20: 'rgba(255,255,255,0.2)',
      5: 'rgba(255,255,255,0.05)',
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          flexDirection: 'row',
          gap: 5,
          alignItems: 'center',
        },
        containedPrimary: {
          '&:hover': {
            color: '#fff',
          },
          '& svg': {
            color: '#fff',
          },
        },
      },
      variants: [
        {
          props: { variant: 'link' },
          style: ({ theme, ownerState }) => ({
            color: theme.palette[ownerState.color]?.main || '#000',
            padding: 0,
            minWidth: 'auto',
            textTransform: 'uppercase',
            textDecoration: 'underline',
            fontWeight: 'normal',
            display: 'inline-flex',
            flexDirection: 'row',
            gap: 5,
            alignItems: 'center',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              color: theme.palette.secondary.main,
            },
          }),
        },
        {
          props: { variant: 'rounded' },
          style: ({ theme, ownerState }) => {
            const color = ownerState.color && ownerState.color !== 'inherit' ? ownerState.color : 'primary';
            const palette = theme.palette[color] || theme.palette.primary;
            const isSmall = ownerState.size === 'small';
            return {
              backgroundColor: palette.main,
              color: palette.contrastText || '#fff',
              borderRadius: 8,
              padding: isSmall ? theme.spacing(0.5, 2) : theme.spacing(2, 4),
              fontWeight: 400,
              letterSpacing: '0.75px',
              ...(isSmall && { fontSize: '10px' }),
              '&:hover': {
                backgroundColor: palette.dark || palette.main,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            };
          },
        },
      ],
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          padding: '13px 15px',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionEnd: {
          marginRight: 15,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '& > a': {
            color: 'rgba(0, 0, 0, 0.8)',
            '&:hover': {
              color: 'rgba(0, 0, 0, 0.8)',
            },
          },
        },
        padding: {
          padding: 20,
          paddingTop: 20,
          paddingBottom: 20,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#525E6A',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#6C7884',
              color: '#fff',
            },
          },
        },
        button: {
          '&:hover': {
            backgroundColor: '#EBF7FF',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
        rounded: {
          borderRadius: 0,
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { color: 'success' },
          style: ({ theme }) => ({
            color: theme.palette.success.main,
          }),
        },
        {
          props: { variant: 'rsp' },
          style: {
            color: '#B6A28F',
            fontSize: 12,
          },
        },
      ],
    },
    MuiLoadingButton: {
      defaultProps: {
        loadingIndicator: <CircularProgress color="inherit" size={24} />,
      },
      styleOverrides: {
        loadingIndicator: ({ ownerState, theme }) => {
          const colorKey = ownerState.color && ownerState.color !== 'inherit' ? ownerState.color : 'primary';
          const palette = theme.palette[colorKey] || theme.palette.primary;
          const isFilledVariant = ['contained', 'rounded'].includes(ownerState.variant);
          return {
            color: isFilledVariant ? alpha(palette.contrastText || '#fff', 0.7) : palette.main,
          };
        },
      },
    },
  },
});
